import { type GetPaths, useClientPortalAPI, useSubmit } from "@certn/api";
import {
  Container,
  ExpandableCard,
  Grid,
  Layout,
  Loader,
  NeutralTag,
  SecondaryTopNav,
  SectionMessage,
  SuccessTag,
} from "@certn/cascadia-react";
import { PrimaryButton, showToast, Stack, Text } from "@certn/cascadia";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { number, object, string } from "yup";

import { ActionsDropdown } from "./ActionsDropdown";
import { AddressBasedTemplateFormFields } from "./AddressBasedTemplateFormFields";
import { MaximumPriceFields } from "./MaximumPriceFields";
import { useCheckArgumentsTemplatesInPackages } from "./useCheckArgumentsTemplatesInPackages";
import {
  ADDRESS_BASED_TEMPLATE_DEFAULTS,
  type AddressBasedTemplateFormValues,
  type AddressBasedTemplatePageConfig,
} from "./types";

interface AddressBasedTemplateDetailPageProps {
  config: AddressBasedTemplatePageConfig;
}

export function AddressBasedTemplateDetailPage({
  config,
}: AddressBasedTemplateDetailPageProps) {
  const {
    apiPathPrefix,
    settingsPathPrefix,
    templateLabel,
    defaultLookbackYears = ADDRESS_BASED_TEMPLATE_DEFAULTS.DEFAULT_LOOKBACK_YEARS,
    showMaximumPrice = false,
  } = config;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams();

  const listPath = `/settings/${settingsPathPrefix}`;
  const apiBase = `/api/internal/client-portal/check-arguments-templates/${apiPathPrefix}`;

  const schema = object().shape({
    name: string().required().label(t("Template name")),
    minimum_duration_months: number()
      .required()
      .min(ADDRESS_BASED_TEMPLATE_DEFAULTS.MIN_DURATION_MONTHS)
      .max(ADDRESS_BASED_TEMPLATE_DEFAULTS.MAX_DURATION_MONTHS)
      .label(t("Minimum length of stay")),
    lookback_years: number()
      .required()
      .min(ADDRESS_BASED_TEMPLATE_DEFAULTS.MIN_LOOKBACK_YEARS)
      .max(ADDRESS_BASED_TEMPLATE_DEFAULTS.MAX_LOOKBACK_YEARS)
      .label(t("Look back period years")),
  });

  const templateIdsBelongingToActivePackages =
    useCheckArgumentsTemplatesInPackages();

  const { data, mutate, isLoading, error } = useClientPortalAPI(
    `${apiBase}/${templateId}/` as GetPaths
  );

  const { data: allTemplates } = useClientPortalAPI(`${apiBase}/` as GetPaths);

  const templateListData = allTemplates as
    | { results: AddressBasedTemplateFormValues[] }
    | undefined;
  const activeTemplates = templateListData?.results?.filter(
    (template) => template.is_active === true
  );

  const { submit } = useSubmit(`${apiBase}/${templateId}/update/`, {
    throwErrors: true,
    showToastOnError: true,
    onSuccess: async () => {
      await mutate();
      showToast(
        t("{{label}} template has been updated.", {
          label: templateLabel(t),
        }),
        { type: "success" }
      );
      navigate(listPath);
    },
  });

  const handleExitForm = () => {
    navigate(listPath);
  };

  if (isLoading) {
    return <Loader overlay />;
  }

  if (error) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t("Unable to retrieve template.")}
        icon="error"
      >
        {error.message}
      </SectionMessage>
    );
  }

  if (!data) {
    return (
      <SectionMessage
        icon="warning"
        sectionTitle={t("Unable to retrieve template information")}
        type="danger"
      />
    );
  }

  const templateData = data as AddressBasedTemplateFormValues;
  const checkArgs = templateData.check_arguments;
  const initialMinDuration =
    (checkArgs?.umbrella_minimum_address_duration_months as
      | number
      | undefined) || ADDRESS_BASED_TEMPLATE_DEFAULTS.DEFAULT_DURATION_MONTHS;
  const initialLookbackYears =
    ((checkArgs?.umbrella_address_history_range_months as number | undefined) ||
      defaultLookbackYears * 12) / 12;
  const initialApplyResidencyRulesToCurrentAddress =
    checkArgs?.umbrella_apply_residency_rules_to_current_address ?? true;
  const initialMaximumPrice = checkArgs?.maximum_price as
    | Record<string, number>
    | null
    | undefined;

  return (
    <Formik
      initialValues={{
        name: templateData.name,
        minimum_duration_months: initialMinDuration,
        lookback_years: initialLookbackYears,
        apply_residency_rules_to_current_address:
          initialApplyResidencyRulesToCurrentAddress,
        ...(showMaximumPrice
          ? {
              enable_maximum_price:
                Object.keys(initialMaximumPrice ?? {}).length > 0,
              maximum_price: initialMaximumPrice ?? {},
            }
          : {}),
      }}
      validationSchema={schema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await submit({ name: values.name });
        } catch (error) {
          console.error(error);
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Layout
          layout="narrow"
          header={
            <SecondaryTopNav
              testID="close-button"
              leftIcon="close"
              onleftIconOnPress={handleExitForm}
            >
              <PrimaryButton
                onPress={() => handleSubmit()}
                isLoading={isSubmitting}
              >
                {t("Save changes")}
              </PrimaryButton>
            </SecondaryTopNav>
          }
        >
          <Container>
            <Stack gap="space-3">
              <Text as="h1" style="global/title-medium">
                {t("{{label}} template details", {
                  label: templateLabel(t),
                })}
              </Text>
            </Stack>

            <Grid form>
              <ExpandableCard
                title={
                  <Stack orientation="horizontal" gap="space-2">
                    {templateData.name}
                    {templateData.is_active ? (
                      <SuccessTag type="subtle">{t("Active")}</SuccessTag>
                    ) : (
                      <NeutralTag type="subtle">{t("Deactivated")}</NeutralTag>
                    )}
                  </Stack>
                }
                subtitle={t("Address-based")}
                rightContent={
                  <ActionsDropdown
                    templateIdsBelongingToActivePackages={
                      templateIdsBelongingToActivePackages
                    }
                    templateId={templateData.id ?? ""}
                    name={templateData.name}
                    isActive={templateData.is_active ?? false}
                    numActiveTemplates={activeTemplates?.length || 0}
                    mutate={mutate}
                    settingsPathPrefix={settingsPathPrefix}
                    apiPathPrefix={apiPathPrefix}
                  />
                }
              />
              <AddressBasedTemplateFormFields
                isDisabled={true}
                showNameField={true}
                defaultLookbackYears={defaultLookbackYears}
              />
              {showMaximumPrice && <MaximumPriceFields isDisabled={true} />}
            </Grid>
          </Container>
        </Layout>
      )}
    </Formik>
  );
}
